import { useEffect, useMemo, useState } from "react";
import { inventoryActions } from "redux/Ldb/actions";
import { useAppDispatch, useAppSelector } from "../storeHooks";
import { debounce, set, sortBy, uniqBy } from "lodash";
import useInfiniteScroll from "../useInfinteScroll";
import { useSearchParams } from "react-router-dom";

const useGetProductList = (scrollContent?: HTMLElement) => {
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchQuery = searchParams.get("search");
    const categoryQuery = searchParams.get("category");
    const channelQuery = searchParams.get("channel");

    const {
        fetchingInventoryList,
        fetchedInventoryListSuccess,
        fetchedInventoryListFailure,
        fetchingInventorySearchList,
        fetchedInventorySearchListSuccess,
        fetchedInventorySearchListFailure,
    } = useAppSelector((state) => state.inventory);

    const [productList, setProductList] = useState<{ [key: string]: any }[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [refetch, setRefetch] = useState<boolean>(false);
    const [productSearchList, setProductSearchList] = useState<{ [key: string]: any }[]>([]);
    const [pagination, setPagination] = useState<{ currentPage: string; noOfPages: string }>({
        currentPage: "1",
        noOfPages: "",
    });
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");
    const [tracking, setTracking] = useState<boolean>(true);
    const [debouncedSearchTwo, setDebouncedSearchTwo] = useState<string>("");
    const [search, setSearch] = useState<string>(searchQuery || "");
    const [searchTwo, setSearchTwo] = useState<string>("");
    const [productCategory, setProductCategory] = useState<string>(categoryQuery || "");
    const [channel, setChannel] = useState<string>(channelQuery || "");
    const [rawProductData, setRawData] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState<{ name?: string; value: string }>({
        name: "",
        value: "",
    });

    const { updatedLimit, isLoadingMore, setLoadMore, updatedPage, setUpdatedPage } =
        useInfiniteScroll(
            { current: pagination.currentPage, number_of_pages: pagination.noOfPages },
            10,
            scrollContent
        );

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUpdatedPage(1);
        setSearch(e.target.value);
    };

    const handleSearchTwo = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUpdatedPage(1);
        setSearchTwo(e.target.value);
    };

    const handleDebouncedChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        setDebouncedSearch(e.target.value);
        setSearchParams({
            search: e.target.value,
            category: productCategory,
            channel: channel,
        });
    }, 1000);

    const handleDebouncedChangeTwo = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        setDebouncedSearchTwo(e.target.value);
    }, 1000);

    const handleChannelChange = (value: string) => {
        setUpdatedPage(1);
        setProductList([]);
        dispatch(
            inventoryActions.fetchInventoryList(
                10,
                1,
                debouncedSearch,
                productCategory,
                value,
                tracking
            )
        );
        setChannel(value);
        setSearchParams({
            search: search,
            category: productCategory,
            channel: value,
        });
    };

    const handleFilterChange = (value: string) => {
        setUpdatedPage(1);
        setProductList([]);
        dispatch(
            inventoryActions.fetchInventoryList(10, 1, debouncedSearch, value, channel, tracking)
        );
        setProductCategory(value);
        setSearchParams({ search: search, category: value, channel: channel });
    };

    const handleClearChannel = () => {
        setUpdatedPage(1);
        setProductList([]);
        dispatch(
            inventoryActions.fetchInventoryList(
                10,
                1,
                debouncedSearch,
                productCategory,
                "",
                tracking
            )
        );
        setChannel("");
        setSearchParams({ search: search, category: productCategory, channel: "" });
    };

    const handleClearFilter = () => {
        setUpdatedPage(1);
        setProductList([]);
        dispatch(
            inventoryActions.fetchInventoryList(10, 1, debouncedSearch, "", channel, tracking)
        );
        setProductCategory("");
        setSearchParams({ search: search, category: "", channel: channel });
    };

    const handleFilterChangeTwo = (name: string, value: string) => {
        setSelectedProduct({ name, value });
    };

    const handleClearFilterTwo = () => {
        setSelectedProduct({ name: "", value: "" });
    };

    const handleSortByStockoutDate = (type: 1 | -1) => {
        setUpdatedPage(1);
        setProductList([]);
        dispatch(
            inventoryActions.fetchInventoryList(
                10,
                1,
                debouncedSearch,
                productCategory,
                channel,
                tracking,
                type
            )
        );
    };

    // useEffect(() => {
    //     setSearch(searchQuery);
    //     setProductCategory(categoryQuery);
    //     setChannel(channelQuery);
    // }, [channelQuery, searchQuery, categoryQuery]);

    useEffect(() => {
        dispatch(
            inventoryActions.fetchInventoryList(
                10,
                updatedPage,
                searchQuery || debouncedSearch,
                categoryQuery || productCategory,
                channelQuery || channel,
                tracking
            )
        );

        return () => {
            dispatch(inventoryActions.resetFetchInventoryList());
        };
    }, [
        dispatch,
        debouncedSearch,
        updatedPage,
        productCategory,
        channel,
        tracking,
        refetch,
        channelQuery,
        searchQuery,
        categoryQuery,
    ]);

    useEffect(() => {
        dispatch(inventoryActions.fetchInventorySearchList(10, debouncedSearchTwo));
    }, [dispatch, debouncedSearchTwo]);

    useEffect(() => {
        if (Boolean(fetchedInventoryListSuccess)) {
            setProductList((prev) =>
                updatedPage === 1
                    ? uniqBy([...fetchedInventoryListSuccess.inventory?.data], "_id")
                    : uniqBy([...prev, ...fetchedInventoryListSuccess.inventory?.data], "_id")
            );

            setPagination({
                currentPage: fetchedInventoryListSuccess.pagination?.current,
                noOfPages: fetchedInventoryListSuccess.pagination?.number_of_pages,
            });
            setTotal(fetchedInventoryListSuccess?.total);
            setRawData(fetchedInventoryListSuccess.inventory?.data);
            setLoadMore(false);
            dispatch(inventoryActions.resetFetchInventoryList());
        }
    }, [dispatch, fetchedInventoryListSuccess, updatedPage, debouncedSearch, productCategory]);

    useEffect(() => {
        if (Boolean(fetchedInventorySearchListSuccess)) {
            setProductSearchList(fetchedInventorySearchListSuccess.inventory?.data);
            // setPagination({
            //     currentPage: fetchedInventoryListSuccess.pagination?.current,
            //     noOfPages: fetchedInventoryListSuccess.pagination?.number_of_pages,
            // });
            // setLoadMore(false);
        }
    }, [fetchedInventorySearchListSuccess]);

    return {
        isFetchingSecondList: fetchingInventorySearchList,
        isFetching: fetchingInventoryList,
        error: fetchedInventoryListFailure,
        data: productList,
        rawData: rawProductData || [],
        productSearchList,
        setProductList,
        setUpdatedPage,
        pagination,
        setPagination,
        debouncedSearch,
        search,
        setSearch,
        setDebouncedSearch,
        searchTwo,
        setSearchTwo,
        setDebouncedSearchTwo,
        productCategory,
        handleDebouncedChange,
        handleDebouncedChangeTwo,
        handleSearch,
        handleSearchTwo,
        handleFilterChange,
        handleFilterChangeTwo,
        handleClearFilter,
        handleClearFilterTwo,
        selectedProduct,
        isLoadingMore,
        updatedPage,
        channel,
        total,
        handleChannelChange,
        handleClearChannel,
        handleSortByStockoutDate,
        setTracking,
        setRefetch,
    };
};

export default useGetProductList;
